import React, { useMemo, useState } from "react";

import { MdClose } from "react-icons/md";
import { useWindowSize } from "@uidotdev/usehooks";
import { Box, Card, Stack, Typography, CircularProgress } from "@mui/material";
import FiliationTable from "@/taskpane/modules/folder/filiation/FiliationTable";
import StyledTextField from "@/taskpane/components/styled-text-field/StyledTextField";
import FiliationMultiselect from "@/taskpane/modules/folder/filiation/FiliationMultiselect";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import styles from "./FiliationList.styles";

import type { FiliationListProps } from "./FiliationList.types";

export default function FiliationList({
  blockUi,
  colNum,
  color,
  data,
  indicator,
  loading,
  noDataMessage,
  onSelectProperty,
  selectedProperties,
  title,
  type,
  handleChange,
  expanded,
}: FiliationListProps) {
  const [search, setSearch] = useState("");
  const [selectedParcelles, setSelectedParcelles] = useState<number[]>([]);
  const [selectedVolumes, setSelectedVolumes] = useState<number[]>([]);

  const { height } = useWindowSize();

  const allParcelles = (data ?? [])
    .filter((d) => d?.parents)
    .map((d) => d.parents.filter((p) => p?.type === "cadastre"))
    .flat();

  const parcelles = Array.from(new Set(allParcelles.map((p) => p.id)))
    .map((id) => allParcelles.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined)
    .sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''));

  const allVolumes = (data ?? [])
    .filter((d) => d?.parents)
    .map((d) => d.parents.filter((p) => p?.type === "volume"))
    .flat();

  const volumes = Array.from(new Set(allVolumes.map((v) => v.id)))
    .map((id) => allVolumes.find((v) => v.id === id))
    .filter((v): v is NonNullable<typeof v> => v !== undefined);


  /**
   * @description Compute the maximum height for each table accordion.
   * 
   * Uses moderate heights to show content while allowing page scroll.
   * Each accordion scrolls internally if content exceeds max height.
   *
   * @returns {string} The max height of the content.
   */
  const computeMaxHeight = useMemo(() => {
    if (height === null) return "400px";
    
    // Moderate heights that show enough content per accordion
    // Page will scroll naturally to access buttons
    const breakpoints = [
      { min: 1400, height: "500px" },
      { min: 1200, height: "450px" },
      { min: 1000, height: "400px" },
      { min: 800, height: "350px" },
      { min: 600, height: "300px" },
      { min: 400, height: "250px" }
    ];
    
    const breakpoint = breakpoints.find(bp => height > bp.min);
    return breakpoint ? breakpoint.height : "200px";
  }, [height]);

  return (
      <Accordion expanded={expanded === type} onChange={handleChange(type)} disableGutters>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id={`${type}-header`}
          sx={{ px: "4px", minHeight: "36px" }}
        >
          <Box>
            <Typography fontSize={13} color={`${color}.600`} fontWeight={600} sx={{ mt: 0 }}>
              {title} {loading ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : ` (${data?.length ?? 0})`}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: "4px", pb: "4px", pt: 0 }}>
          <Stack spacing={0} sx={{ maxHeight: computeMaxHeight, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Card sx={{ ...styles.card, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Tables actions */}
              <Stack spacing={1} direction="row" sx={{ flexShrink: 0 }}>

                {/* Search bar */}
                <Box flexGrow={1} sx={type !== "cadastre" ? { maxWidth: "50%" } : undefined}>
                  <StyledTextField
                    disabled={!!blockUi}
                    label="Recherche"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    InputProps={{
                      endAdornment: search && (
                        <MdClose size={16} color="grey" style={styles.closeIcon} onClick={() => setSearch("")} />
                      ),
                    }}
                  />
                </Box>

                {/* Lot filter */}
                {type === "lot" && (
                  <Box flexGrow={2}>
                    <FiliationMultiselect
                      blockUi={blockUi}
                      data={volumes}
                      selectedElements={selectedVolumes}
                      setSelectedElements={setSelectedVolumes}
                      color="warning"
                      title="Volumes"
                    />
                  </Box>
                )}

                {/* Lots/Volumes filter */}
                {(type === "volume" || type === "lot") && (
                  <Box flexGrow={2}>
                    <FiliationMultiselect
                      blockUi={blockUi}
                      data={parcelles}
                      selectedElements={selectedParcelles}
                      setSelectedElements={setSelectedParcelles}
                      color="success"
                      title="Parcelles"
                    />
                  </Box>
                )}

              </Stack>

              {/* Filiation Table */}
              <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
                <FiliationTable
                  blockUi={blockUi}
                  data={data}
                  type={type}
                  onSelectProperty={onSelectProperty}
                  selectedProperties={selectedProperties}
                  noDataMessage={noDataMessage}
                  loading={loading}
                  colNum={colNum}
                  filters={{
                    search,
                    parcelleIds: selectedParcelles,
                    volumeIds: selectedVolumes,
                  }}
                />
              </Box>

            </Card>
          </Stack>
        </AccordionDetails>
      </Accordion>
  );
}
